import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from './cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leadingIcon?: ReactNode;
  fullWidth?: boolean;
}

// Pill buttons — ink-black primary, ghost, danger per the Stackly design.
const VARIANTS: Readonly<Record<Variant, string>> = {
  primary:
    'bg-ink text-warm-white hover:opacity-80 active:scale-[0.98] disabled:bg-warm-300 disabled:hover:opacity-100',
  secondary: 'bg-ink/[0.07] text-ink hover:bg-ink/[0.11] active:scale-[0.98]',
  ghost: 'bg-transparent text-ink border border-current hover:bg-ink/[0.05]',
  danger: 'bg-coral text-warm-white hover:opacity-80 active:scale-[0.98]',
};

const SIZES: Readonly<Record<Size, string>> = {
  sm: 'h-8 px-4 text-[12px] gap-1.5 font-semibold tracking-wide uppercase',
  md: 'h-10 px-5 text-[13px] gap-2 font-semibold tracking-wide uppercase',
  lg: 'h-12 px-6 text-[14px] gap-2 font-semibold tracking-wide uppercase',
};

/**
 * Pill button. Matches the design's `cta-dark` / `cta-ghost` / `add-tab`
 * treatments — always pill-shaped (`rounded-button` = 100px).
 */
export const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leadingIcon,
  fullWidth = false,
  className,
  disabled,
  children,
  type,
  ...rest
}: ButtonProps) => {
  return (
    <button
      type={type ?? 'button'}
      disabled={disabled ?? loading}
      className={cn(
        'inline-flex items-center justify-center rounded-button transition-all duration-150 ease-notion',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : leadingIcon}
      {children}
    </button>
  );
};
